import React, { useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import AppRouter from "./components/appRouter/AppRouter";
import Sidebar from "./components/UI/sidebar/Sidebar";
import "./styles/css/App.css";
import "./styles/css/pop-up-window.css";
import "@fontsource/roboto";
import Colors from "./values/Colors";
import QrScanner from "./components/UI/qrCode/qrScanner/QrScanner";
import NavbarProfile from "./components/UI/navbar/navbarProfile/NavbarProfile";
import NavbarAdmin from "./components/UI/navbar/navbarAdmin/NavbarAdmin";
import env from "../package.json";
import { setSidebarStatus } from "redux/slice/sidebarSlice";
import { useDeviceType } from "components/hooks/useDeviceType";
import { RefMainContainerContext } from "context";
import Footer from "components/UI/footer/Footer";
import OAuthService from "services/OAuthService";
import Notifications from "components/UI/popupNotifications/Notifications";
import CommandService from "services/CommandService";
import "./styles/scss/variables.css";
import "./styles/values.css";
import Strings from "values/Strings";
import { BrandingProvider } from "components/UI/branding/BrandingProvider";
import BrandingStyles from "components/UI/branding/BrandingStyles";
import "styles/css/smooth-transitions.css";

function App() {
  const title = useSelector((state) => state.title.text);
  const addressBarName1 = window.location.pathname.slice(1).split("/")[0];
  const addressBarName2 = window.location.pathname.slice(1).split("/")[1];
  const onScrollBar = useSelector((state) => state.scrollBar.status);
  const cameraStatus = useSelector((state) => state.camera.isCamera);
  const sidebarStatus = useSelector((state) => state.sidebar.isActive);
  // const loader = useSelector(state => state.loader.status)
  const sidebarItemName = useSelector((state) => state.sidebarItem.item);
  const popUpWindowStatus = useSelector((state) => state.popUpWindow.status);

  const userNavbarList = useMemo(() => [
    Strings.sidebarGrade,
    Strings.sidebarProfile,
    Strings.sidebarMain,
  ], []);
  const adminNavbarList = useMemo(() => [Strings.sidebarAdminPanel], []);
  const navbarNameList = useMemo(() => ["admin", "profile", "team-grade-page"], []);
  const mainNavbarList = useMemo(() => ["main", "logout"], []);
  const noHeaderPages = useMemo(() => ["main/info", "admin/team-management"], []); // Страницы без header
  const device = useDeviceType();
  const dispatch = useDispatch();
  const mainContainerRef = useRef(null);

  const wrapperClassName = [
    "wrapper",
    device !== "phone"
      ? sidebarStatus
        ? "wrapper--sidebar-open"
        : "wrapper--sidebar-closed"
      : null,
  ]
    .filter(Boolean)
    .join(" ");

  // Определяем, есть ли navbar на текущей странице
  const hasNavbar =
    !noHeaderPages.includes(`${addressBarName1}/${addressBarName2}`) &&
    (adminNavbarList.includes(sidebarItemName) ||
      navbarNameList.includes(addressBarName1) ||
      navbarNameList.includes(addressBarName2) ||
      userNavbarList.includes(sidebarItemName) ||
      (mainNavbarList.includes(addressBarName1) && addressBarName1 !== "main"));


  function closeSideBar() {
    device === "phone" && dispatch(setSidebarStatus(false));
  }

  useEffect(() => {
    const checkOAuth = async () => {
      try {
        const res = await OAuthService.refreshAuth();
        const userData = res?.userData;
        localStorage.setItem("accessToken", res.accessToken);
        userData
          ? localStorage.setItem("userData", userData)
          : localStorage.removeItem("userData");
        const commandRes = await CommandService.checkCommand();
        commandRes
          ? localStorage.setItem("command", commandRes)
          : localStorage.removeItem("command");
      } catch (error) {
        localStorage.removeItem("userData");
        localStorage.removeItem("command");
      }
    };

    const id = localStorage.getItem("accessToken");
    if (id) {
      checkOAuth();
    }
    dispatch(
      setSidebarStatus(device === "desktop" && window.innerWidth > 1281)
    );
  }, [device, dispatch]);

  // Установка CSS переменных для динамических стилей
  useEffect(() => {
    const root = document.documentElement;
    const isDesktop = device === "desktop" && window.innerWidth > 1281;
    const isPhone = device === "phone";
    const currentPage = `${addressBarName1}/${addressBarName2}`;
    const isMainInfoPage = currentPage === "main/info";
    const hasAdminNavbar = adminNavbarList.includes(sidebarItemName) || navbarNameList.includes(addressBarName1);
    const hasUserNavbar = userNavbarList.includes(sidebarItemName) || navbarNameList.includes(addressBarName2) || (mainNavbarList.includes(addressBarName1) && addressBarName1 !== "main");
    const hasAnyNavbar = hasAdminNavbar || hasUserNavbar || (mainNavbarList.includes(addressBarName1) && addressBarName1 !== "main");
    const shouldShowNavbar = !noHeaderPages.includes(currentPage) && hasAnyNavbar;

    // Wrapper overflow
    root.style.setProperty("--wrapper-overflow", popUpWindowStatus ? "hidden" : "visible");

    // Sidebar background
    root.style.setProperty("--sidebar-bg-color", Colors.white);

    // Content styles
    // На мобилке используем 12vw (высота navbar), на десктопе 10vh
    const contentPaddingTop = shouldShowNavbar
      ? isPhone ? "12vw" : "10vh"
      : "0";

    // Объединяем padding-top с остальными padding значениями
    const contentPadding = isMainInfoPage
      ? `${contentPaddingTop} 0px ${isDesktop ? '0' : '20px'} 0px`
      : `${contentPaddingTop} ${isDesktop ? '20px' : '0px'} 12vw ${isDesktop ? '20px' : '0px'}`;
    root.style.setProperty("--content-padding", contentPadding);

    // Navbar styles
    root.style.setProperty("--navbar-bg-color", Colors.white);
    // Высота navbar на мобилке для отступа контента (используется в PageWrapper.module.scss)
    root.style.setProperty("--navbar-height-mobile", isPhone && shouldShowNavbar ? "12vw" : "0");

    // Title styles
    root.style.setProperty("--title-color", Colors.white);
    root.style.setProperty("--title-margin-right", onScrollBar ? "5px" : "0");


    // Footer styles
    const footerMarginLeft = isDesktop
      ? sidebarStatus ? "18.1vw" : `${env.sidebar.minWidth - 2}vw`
      : "0";
    root.style.setProperty("--footer-margin-left", footerMarginLeft);

    const footerWidth = isDesktop
      ? sidebarStatus
        ? "calc(100% - 18.1vw)"
        : `calc(100% - ${env.sidebar.minWidth - 2}vw)`
      : "100%";
    root.style.setProperty("--footer-width", footerWidth);
  }, [
    device,
    sidebarStatus,
    popUpWindowStatus,
    onScrollBar,
    hasNavbar,
    addressBarName1,
    addressBarName2,
    sidebarItemName,
    adminNavbarList,
    navbarNameList,
    userNavbarList,
    mainNavbarList,
    noHeaderPages,
  ]);

  return (
    // <AppLoader status={loadingOAuth}>
    <BrandingProvider>
      <BrandingStyles />
      <RefMainContainerContext.Provider value={{ mainContainerRef }}>
        <BrowserRouter>
          {cameraStatus === true ? (
            <QrScanner />
          ) : (
            <div className={wrapperClassName}>
              <div className="sidebar">
                <Sidebar />
              </div>
              <div className="content">
                {/* Проверяем, нужно ли показывать header для текущей страницы */}
                {!noHeaderPages.includes(
                  `${addressBarName1}/${addressBarName2}`
                ) && (
                    <>
                      {adminNavbarList.includes(sidebarItemName) ||
                        navbarNameList.includes(addressBarName1) ? (
                        <nav className="navbar-container">
                          <NavbarAdmin />
                        </nav>
                      ) : userNavbarList.includes(sidebarItemName) ||
                        navbarNameList.includes(addressBarName2) ||
                        (mainNavbarList.includes(addressBarName1) &&
                          addressBarName1 !== "main") ? (
                        <nav className="navbar-container">
                          <NavbarProfile
                            pageName={[addressBarName2, addressBarName1]}
                          />
                        </nav>
                      ) : (
                        title !== "Общая информация о мероприятии" &&
                        addressBarName1 !== "main" && (
                          <div className="title">
                            <p>{title}</p>
                          </div>
                        )
                      )}
                    </>
                  )}
                <div
                  className="content__container"
                  ref={mainContainerRef}
                  onClick={closeSideBar}
                >
                  <AppRouter />
                </div>
              </div>
              {(addressBarName2
                ? !addressBarName2.includes("team-grade-page")
                : true) && (
                  <div
                    className={`footer-container ${popUpWindowStatus && "pop-up-blure"}`}
                  >
                    <nav className="footer">
                      <Footer />
                    </nav>
                  </div>
                )}
            </div>
          )}
          <Notifications />
        </BrowserRouter>
      </RefMainContainerContext.Provider>
    </BrandingProvider>
    // </AppLoader >
  );
}

export default App;
